import React, { useEffect, useState } from 'react';
import api from '../api';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const EventBook = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const evRes = await api.get(`/api/events`);
        const found = evRes.data.find(e => (e._id === eventId || e.id === eventId));
        setEvent(found);

        const mediaRes = await api.get(`/api/media/event/${eventId}?status=approved`);
        setMedia(mediaRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [eventId]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) return (
    <div className="container py-5 text-center" dir="rtl">
       <div className="spinner-grow text-primary mb-3" role="status"></div>
       <p className="fs-4 fw-bold text-primary">جاري تحضير كتاب ذكرياتك بكل حب... ✨</p>
    </div>
  );

  if (!event) return <div className="text-center py-5">المناسبة مش موجودة.</div>;

  const photos = media.filter(m => m.type === 'photo');
  const quotes = media.filter(m => m.type === 'text');
  const voices = media.filter(m => m.type === 'voice');

  return (
    <div className="container py-3 py-md-5 book-view" dir="rtl">
      <div className="d-print-none mb-4 mb-md-5 d-flex flex-column flex-md-row justify-content-between align-items-center bg-white p-3 rounded-celebration shadow-sm border border-primary-dim gap-3">
        <Link
          to="/admin"
          className="btn btn-outline-primary fw-bold w-100 w-md-auto"
        >
          ← رجوع للوحة التحكم
        </Link>
        <button
          onClick={handlePrint}
          className="btn btn-primary fw-bold px-5 shadow-lg rounded-pill w-100 w-md-auto"
        >
          🖨️ طباعة أو حفظ كـ PDF
        </button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="card shadow-lg border-0 p-3 p-md-5 book-content bg-white"
        style={{
          minHeight: "100vh",
          fontFamily: "var(--font-headline)",
          borderRadius: "var(--radius-celebration)",
        }}
      >
        <header className="text-center mb-5 border-bottom pb-5 position-relative border-primary-dim">
          {event.eventPhoto ? (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="promo-cover mx-auto mb-4 p-2 bg-white shadow-xl border border-primary-dim"
              style={{
                width: "min(100%, 500px)",
                borderRadius: "15px",
                rotate: "-1deg",
              }}
            >
              <img
                src={event.eventPhoto}
                alt="Promo"
                className="w-100 h-100 object-cover rounded-3"
                style={{ maxHeight: "300px" }}
              />
            </motion.div>
          ) : (
            <div className="mb-4 text-primary fs-1">📔</div>
          )}
          <h1
            className="display-4 display-md-3 fw-black text-primary mb-3"
            style={{ fontFamily: "var(--font-headline)" }}
          >
            {event.name}
          </h1>
          <p className="fs-4 text-on-surface-variant mb-4 arabic-leading">
            كتاب الذكريات الرقمي
          </p>
          {event.clientName && (
            <p className="fs-5 fw-bold text-dark">باسم: {event.clientName}</p>
          )}
          {event.locationName && (
            <p className="fs-5 text-muted">
              📍{" "}
              {event.locationGoogleMapsLink ? (
                <a
                  href={event.locationGoogleMapsLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-decoration-none"
                >
                  {event.locationName}
                </a>
              ) : (
                event.locationName
              )}
            </p>
          )}
          <div className="badge bg-primary px-4 py-2 fs-5 rounded-pill shadow-sm">
            {new Date(event.eventDate || event.createdAt).toLocaleDateString(
              "ar-EG",
              { dateStyle: "full" }
            )}
          </div>
        </header>

        {/* Quotes Section */}
        {quotes.length > 0 && (
          <section className="mb-5 py-4">
            <h2
              className="text-center fw-black mb-5 display-5 border-bottom border-primary-dim d-inline-block pb-2 text-primary"
              style={{ fontFamily: "var(--font-headline)" }}
            >
              أجمل التهاني والكلمات ❤️
            </h2>
            <div className="row g-4 justify-content-center">
              {quotes.map((q, i) => (
                <div key={i} className="col-12 col-md-6">
                  <motion.div
                    className="p-4 p-md-5 bg-surface-variant rounded-4 shadow-sm border-start border-primary border-5 h-100 position-relative"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="position-absolute top-0 end-0 p-3 opacity-10 fs-1">
                      "
                    </div>
                    <p className="fs-4 fs-md-3 mb-4 lh-base fw-bold">
                      "{q.content}"
                    </p>
                    <footer className="text-end fw-bold text-primary fs-5 mt-auto">
                      — {q.guestName}
                    </footer>
                  </motion.div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Photos Grid */}
        {photos.length > 0 && (
          <section className="mb-5 py-4">
            <h2
              className="text-center fw-black mb-5 display-5 border-bottom border-primary-dim d-inline-block pb-2 text-primary"
              style={{ fontFamily: "var(--font-headline)" }}
            >
              لحظات لا تُنسى 📸
            </h2>
            <div className="row g-3 g-md-4">
              {photos.map((p, i) => (
                <div key={i} className="col-6 col-md-4 col-lg-3">
                  <motion.div
                    className="shadow rounded-4 overflow-hidden border bg-white p-2"
                    whileHover={{ rotate: i % 2 === 0 ? 2 : -2, scale: 1.05 }}
                  >
                    <div className="ratio ratio-1x1 rounded-3 overflow-hidden mb-2">
                      <img
                        src={p.url}
                        alt="memory"
                        style={{ objectFit: "cover" }}
                      />
                    </div>
                    <p className="small text-center mt-2 text-muted fw-bold mb-1 text-truncate px-1">
                      {p.guestName}
                    </p>
                  </motion.div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Voice Notes Summary */}
        {voices.length > 0 && (
          <section className="mb-5 py-4 d-print-none bg-surface-variant rounded-4 p-4 p-md-5">
            <h2
              className="text-center fw-black mb-5 display-5 border-bottom border-primary-dim d-inline-block pb-2 text-primary"
              style={{ fontFamily: "var(--font-headline)" }}
            >
              رسائل صوتية (اسمعها دلوقتي) 🎙️
            </h2>
            <div className="row g-3">
              {voices.map((v, i) => (
                <div key={i} className="col-12">
                  <div className="bg-white p-2 p-md-3 rounded-pill shadow-sm d-flex flex-column flex-md-row align-items-center justify-content-between px-4 border border-primary-dim gap-2">
                    <span className="fw-bold fs-5 text-on-surface">
                      {v.guestName}
                    </span>
                    <audio
                      src={v.url}
                      controls
                      className="ms-md-3 flex-grow-1"
                      style={{ maxHeight: "35px", width: "100%" }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center mt-4 text-muted small">
              ملاحظة: الرسائل الصوتية ما بتظهرش في الطباعة الورقية، بس بتفضل
              ذكرى هنا للأبد.
            </p>
          </section>
        )}

        <footer className="text-center mt-5 pt-5 border-top border-primary-dim text-muted">
          <div
            className="display-4 text-primary mb-3 fw-black"
            style={{ fontFamily: "var(--font-headline)" }}
          >
            يومك
          </div>
          <p className="fw-bold fs-5">
            تم صنع هذا الكتاب بكل حب بواسطة منصة يومك
          </p>
          <p className="small">
            &copy; {new Date().getFullYear()} جميع الحقوق محفوظة لـ يومك
          </p>
        </footer>
      </motion.div>

      <style>{`
        @media print {
          body { background: white !important; padding: 0 !important; margin: 0 !important; }
          .book-view { padding: 0 !important; max-width: 100% !important; margin: 0 !important; }
          .book-content { box-shadow: none !important; border: none !important; padding: 0 !important; }
          .d-print-none { display: none !important; }
          .card { border: none !important; border-radius: 0 !important; }
          header { margin-top: 30px; }
          img { break-inside: avoid; }
          section { break-inside: avoid; }
          .ratio-1x1 { height: 180px !important; }
        }
        .italic { font-style: italic; }
      `}</style>
    </div>
  );
};

export default EventBook;
